import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Maybe, ProductBadgeV2 } from '@storefront/lib-global/codegen/types';
import { useLocaleContext } from '@storefront/lib-global/context/localeContext';
import { useStaticContext } from '@storefront/lib-global/context/staticContext';
import { ImageBadge, TextualBadge } from '@storefront/lib-global/globalComponents/badges';
import { ArrowTailLeft, ArrowTailRight } from '@storefront/lib-global/icons';
import ZoomIcon from '@storefront/lib-global/icons/Zoom/Active.svg';
import { ImagePartial } from '@storefront/lib-global/partials/image';
import { sendViewProductImageEvent } from '@storefront/lib-global/utils/gtm/events/view_product_image/sendViewProductImageEvent';
import { useOccasionAndDenominationContext } from '../../../context';
import { isProductMediaVideo } from '../../../utils/isProductMediaVideo';
import ProductMediaVideo from '../ProductMediaVideo';
import {
  MediaContainer,
  MediaImageGrid,
  MediaImageGridItem,
  MediaImageGridItemHighlight,
  MediaSlide,
  MobileZoomContainer,
  NavigationButton,
  NavigationButtonIcon,
  PaginationContainer,
  ZoomButton,
} from './ProductMedia.styles';

export type ProductMediaProps = {
  productMediaUrls: string[];
  setIsZoomActive: Dispatch<SetStateAction<boolean>>;
  isPaginationDynamic?: boolean;
  activeMediaSlide: number;
  setActiveMediaSlide: (value: number) => void;
  productKey?: string;
  productName?: string;
  isPDPGiftCardType?: boolean;
  subType?: string;
  badgesV2?: Maybe<ProductBadgeV2>;
};

export const ProductMedia = forwardRef(
  (
    {
      productMediaUrls,
      setIsZoomActive,
      isPaginationDynamic,
      activeMediaSlide,
      setActiveMediaSlide,
      isPDPGiftCardType,
      badgesV2,
      productKey,
      productName,
      subType,
    }: ProductMediaProps,
    ref,
  ) => {
    const swiperMediaRef = ref as MutableRefObject<SwiperType | undefined>;
    const [dynamicPaginationXPosition, setDynamicPaginationXPosition] = useState('');
    const { selectedOccasionVariant } = useOccasionAndDenominationContext();
    const { asPath } = useRouter();
    const { locale } = useLocaleContext();
    const { host } = useStaticContext();

    const handleDynamicPaginationPosition = (index: number) => {
      switch (index) {
        case 0:
          return setDynamicPaginationXPosition('translateX(-270%)');
        case 1:
          return setDynamicPaginationXPosition('translateX(-140%)');
        case productMediaUrls.length - 2:
          return setDynamicPaginationXPosition('translateX(140%)');
        case productMediaUrls.length - 1:
          return setDynamicPaginationXPosition('translateX(270%)');
        default:
          return setDynamicPaginationXPosition('translateX(0%)');
      }
    };

    const selectGridSlide = (index: number) => () => {
      swiperMediaRef.current?.slideTo(index);
      setActiveMediaSlide(index);
    };

    const mediaVideosRef = useRef<(HTMLVideoElement | null)[]>([]);
    const gridMediaRef = useRef<(HTMLVideoElement | null)[]>([]);

    const openZoom = () => {
      setIsZoomActive(true);
    };

    const onImageClick = (index: number) => () => {
      sendViewProductImageEvent({
        component_type: 'PDP - Product Image',
        item_name: productName ?? '',
        item_id: productKey ?? '',
        link_url: `https://${host}/${locale}${asPath}`,
        index,
      });
    };

    const onNavigationClick = (index: number) => () => {
      setActiveMediaSlide(index);
      onImageClick(index)();
    };

    useEffect(() => {
      if (!isPDPGiftCardType) {
        swiperMediaRef.current?.slideTo(0);
      }
    }, [swiperMediaRef, isPDPGiftCardType]);

    useEffect(() => {
      if (!isPDPGiftCardType) {
        swiperMediaRef.current?.slideTo(activeMediaSlide);
      }
    }, [activeMediaSlide, swiperMediaRef, isPDPGiftCardType]);

    const isActiveSlideVideo = isProductMediaVideo(productMediaUrls[activeMediaSlide]);

    return (
      <section>
        <MediaContainer
          data-testid="productMediaContainer"
          className="swiper-container"
          $isPaginationDynamic={isPaginationDynamic}
          $dynamicPaginationXPosition={isPaginationDynamic ? dynamicPaginationXPosition : 'unset'}
        >
          <TextualBadge badge={badgesV2?.textual} pdpBadge isVideo={isActiveSlideVideo} />
          <ImageBadge badge={badgesV2?.image} pdpBadge isVideo={isActiveSlideVideo} />

          <div id="data-monetate" />
          {!isPDPGiftCardType ? (
            <Swiper
              onBeforeInit={(swiper) => {
                swiperMediaRef.current = swiper;
              }}
              onActiveIndexChange={(swiper) => {
                selectGridSlide(swiper.activeIndex);
                setActiveMediaSlide(swiper.activeIndex);
              }}
              onSlideChange={(swiper) => {
                handleDynamicPaginationPosition(swiper.activeIndex);
              }}
              modules={[Pagination, Navigation, A11y]}
              slidesPerView="auto"
              navigation={{ prevEl: '.pdp-media-prev', nextEl: '.pdp-media-next' }}
              pagination={{
                el: '.pdp-media-pagination',
                dynamicBullets: isPaginationDynamic,
                dynamicMainBullets: 1,
              }}
              draggable
            >
              {productMediaUrls?.map((mediaUrl, i) => (
                <SwiperSlide key={`${mediaUrl}_${i}`}>
                  <MediaSlide>
                    {isProductMediaVideo(mediaUrl) ? (
                      <ProductMediaVideo
                        videoUrl={mediaUrl}
                        hasControls
                        autoPlay
                        ref={(el) => {
                          mediaVideosRef.current[i] = el;
                        }}
                        observerOptions={{ threshold: 0.7 }}
                      />
                    ) : (
                      <ImagePartial
                        key={mediaUrl}
                        lazyLoading={i !== 0}
                        src={mediaUrl}
                        layout="fill"
                        altText={`${productName} ${subType}, view ${i + 1} of ${productMediaUrls.length}`}
                      />
                    )}
                  </MediaSlide>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <MediaSlide>
              <ImagePartial
                src={selectedOccasionVariant.imageUrls?.[0] ?? productMediaUrls?.[0]}
                lazyLoading={false}
                layout="fill"
                objectFit="contain"
                altText={`${productName} ${subType}`}
              />
            </MediaSlide>
          )}
          {!isPDPGiftCardType && (
            <>
              <NavigationButton
                type="button"
                className="pdp-media-prev"
                onClick={onNavigationClick(swiperMediaRef.current?.activeIndex || 0)}
              >
                <NavigationButtonIcon>
                  <ArrowTailRight />
                </NavigationButtonIcon>
              </NavigationButton>
              <NavigationButton
                type="button"
                className="pdp-media-next"
                onClick={onNavigationClick(swiperMediaRef.current?.activeIndex || 0)}
              >
                <NavigationButtonIcon>
                  <ArrowTailLeft />
                </NavigationButtonIcon>
              </NavigationButton>
            </>
          )}
          <MobileZoomContainer>
            <ZoomButton aria-label="open zoom overlay" type="button" onClick={openZoom}>
              <ZoomIcon />
            </ZoomButton>
          </MobileZoomContainer>
          <PaginationContainer>
            <div className="pdp-media-pagination" />
            <ZoomButton aria-label="open zoom overlay" type="button" onClick={openZoom}>
              <ZoomIcon />
            </ZoomButton>
          </PaginationContainer>
        </MediaContainer>
        {!isPDPGiftCardType && (
          <MediaImageGrid>
            {productMediaUrls?.map((mediaUrl, i) => (
              <MediaImageGridItem key={`${mediaUrl}_${i}`} onClick={selectGridSlide(i)}>
                {isProductMediaVideo(mediaUrl) ? (
                  <ProductMediaVideo
                    videoUrl={mediaUrl}
                    ref={(el) => {
                      gridMediaRef.current[i] = el;
                    }}
                  />
                ) : (
                  <ImagePartial
                    src={mediaUrl}
                    layout="fill"
                    onClick={onImageClick(i)}
                    altText={`${productName} ${subType}, view ${i + 1} of ${productMediaUrls.length}`}
                  />
                )}
                <MediaImageGridItemHighlight $selectedSlide={activeMediaSlide === i} />
              </MediaImageGridItem>
            ))}
          </MediaImageGrid>
        )}
      </section>
    );
  },
);

export default ProductMedia;

export function ProductDetailMedia({
  productMediaUrls,
  setIsZoomActive: setZoomActive,
  activeMediaSlide: activeSlide,
  setActiveMediaSlide: setActiveSlide,
  isPDPGiftCardType,
  badgesV2,
  productKey,
  productName,
  subType,
}: Omit<ProductMediaProps, 'isPaginationDynamic'>) {
  const ref = useRef();

  return (
    <ProductMedia
      badgesV2={badgesV2}
      isPDPGiftCardType={isPDPGiftCardType}
      productMediaUrls={productMediaUrls}
      setIsZoomActive={setZoomActive}
      ref={ref}
      activeMediaSlide={activeSlide}
      setActiveMediaSlide={setActiveSlide}
      productKey={productKey}
      productName={productName}
      subType={subType}
    />
  );
}

export function ProductDynamicMedia({
  productMediaUrls,
  setIsZoomActive: setZoomActive,
  activeMediaSlide: activeSlide,
  setActiveMediaSlide: setActiveSlide,
  isPaginationDynamic: paginationDynamic,
  isPDPGiftCardType,
  productKey,
  badgesV2,
  productName,
  subType,
}: ProductMediaProps) {
  const ref = useRef();

  return (
    <ProductMedia
      badgesV2={badgesV2}
      isPDPGiftCardType={isPDPGiftCardType}
      productMediaUrls={productMediaUrls}
      setIsZoomActive={setZoomActive}
      isPaginationDynamic={paginationDynamic}
      ref={ref}
      activeMediaSlide={activeSlide}
      setActiveMediaSlide={setActiveSlide}
      productKey={productKey}
      productName={productName}
      subType={subType}
    />
  );
}
